import type { CodeMigration } from './types.js';

/**
 * Retroactive migration for v5.0.0
 *
 * Normalises Program.cs to the v5 canonical wiring expected by SmartStack 3.51.x.
 *
 * The migration is **idempotent**: running it twice yields the same result. It also
 * tolerates the typical "patched manually over time" shape that older client projects
 * accumulate (e.g. a legacy `AddApplicationInsightsTelemetry` call, an ad-hoc
 * `builder.Services.AddHangfire(...)` block someone added when the runtime crashed,
 * mixed section numbering, etc).
 *
 * Three coupled fixes:
 *
 * 1. Replace `AddApplicationInsightsTelemetry` with
 *    `AddOpenTelemetry().UseAzureMonitor(...)` (legacy SDK → modern OTel distro).
 *
 * 2. Remove any rogue `builder.Services.AddHangfire(...)` block. SmartStack ships
 *    its own canonical wiring via `AddSmartStackHangfire(builder.Configuration)`
 *    which configures the SQL Server storage + dashboard auth. A second AddHangfire
 *    call (e.g. UseInMemoryStorage from an AI patch) shadows the SmartStack one
 *    and causes runtime conflicts.
 *
 * 3. Insert `AddSmartStackHangfire(builder.Configuration)` BEFORE `AddSmartStack`
 *    (IBackgroundJobClient must exist when Infrastructure DI registers
 *    HangfireWorkflowResumeScheduler) and `UseSmartStackHangfireDashboard(...)`
 *    AFTER `UseSmartStack`.
 *
 * Companion change: REQUIRED_PACKAGES.Api ships Hangfire.AspNetCore +
 * Hangfire.SqlServer so `ss upgrade` installs the matching NuGets (they are not
 * transitive from the SmartStack umbrella package: PrivateAssets="all").
 */
export const programCsAzureMonitorMigration: CodeMigration = {
  axis: 'cli',
  fromVersion: '1.0.0',
  toVersion: '5.0.0',
  description: 'Normalise Program.cs to v5 canonical wiring (Azure Monitor + SmartStack Hangfire)',

  migrations: [
    {
      pattern: '**/Program.cs',
      description: 'Migrate to Azure Monitor and normalise Hangfire wiring',

      detect: (content: string): boolean => {
        const usesLegacyAppInsights = content.includes('AddApplicationInsightsTelemetry');
        const hasRogueAddHangfire = hasNonSmartStackAddHangfire(content);
        const callsAddSmartStack = /builder\.Services\.AddSmartStack\s*\(/.test(content);
        const missingHangfire = callsAddSmartStack && !content.includes('AddSmartStackHangfire');
        return usesLegacyAppInsights || hasRogueAddHangfire || missingHangfire;
      },

      transform: (content: string): string => {
        let result = content;

        result = migrateApplicationInsightsToAzureMonitor(result);
        result = removeRogueAddHangfireBlocks(result);
        result = wireSmartStackHangfire(result);

        return result;
      },
    },
  ],
};

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

/**
 * True if Program.cs contains a `builder.Services.AddHangfire(...)` call that is
 * NOT the SmartStack wrapper (`AddSmartStackHangfire`).
 */
function hasNonSmartStackAddHangfire(content: string): boolean {
  // Find any `AddHangfire(` not preceded by `SmartStack`.
  const re = /builder\.Services\.AddHangfire\s*\(/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    const preceding = content.slice(Math.max(0, m.index - 20), m.index);
    if (!preceding.endsWith('SmartStack')) return true;
  }
  return false;
}

function migrateApplicationInsightsToAzureMonitor(content: string): string {
  let result = content;

  result = result.replace(
    /builder\.Services\.AddApplicationInsightsTelemetry\s*\(\s*options\s*=>\s*\{\s*options\.ConnectionString\s*=\s*aiConnectionString\s*;\s*\}\s*\)\s*;/,
    'builder.Services.AddOpenTelemetry()\n            .UseAzureMonitor(options =>\n            {\n                options.ConnectionString = aiConnectionString;\n            });',
  );
  result = result.replace(
    /\/\/\s*Application Insights SDK \(must be added before Serilog runs\)/,
    '// Observability — Azure Monitor via OpenTelemetry (replaces legacy Microsoft.ApplicationInsights.AspNetCore)',
  );
  result = ensureUsingDirective(result, 'Azure.Monitor.OpenTelemetry.AspNetCore');

  return result;
}

/**
 * Strips every rogue `builder.Services.AddHangfire(...)` statement (including any
 * preceding contiguous comment lines / section header). SmartStack's own wiring is
 * preserved because we only match `AddHangfire`, not `AddSmartStackHangfire`.
 */
function removeRogueAddHangfireBlocks(content: string): string {
  let result = content;
  // Hard cap on iterations to avoid runaway loops.
  for (let iter = 0; iter < 5; iter++) {
    const idx = findRogueAddHangfireIndex(result);
    if (idx === -1) break;

    const endIdx = endOfStatement(result, idx);
    if (endIdx === -1) break;

    const startIdx = startOfBlock(result, idx);

    result = result.slice(0, startIdx) + result.slice(endIdx);
  }
  return result;
}

function findRogueAddHangfireIndex(content: string): number {
  const re = /builder\.Services\.AddHangfire\s*\(/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    const preceding = content.slice(Math.max(0, m.index - 20), m.index);
    if (!preceding.endsWith('SmartStack')) return m.index;
  }
  return -1;
}

/**
 * Given the index of a statement start, walks forward to find the matching `;`
 * after a balanced parenthesis group. Returns the index just after the trailing
 * newline (if any), so the caller can splice the segment out cleanly.
 */
function endOfStatement(content: string, startIdx: number): number {
  // Skip the call name to the opening paren.
  const openParen = content.indexOf('(', startIdx);
  if (openParen === -1) return -1;

  let depth = 1;
  let i = openParen + 1;
  while (i < content.length && depth > 0) {
    const c = content[i];
    if (c === '(') depth++;
    else if (c === ')') depth--;
    else if (c === '"') {
      // Skip strings to avoid being confused by ')' or '(' inside them.
      i = skipString(content, i);
      continue;
    }
    i++;
  }
  if (depth !== 0) return -1;

  // Walk past whitespace + the closing `;`.
  while (i < content.length && /[ \t]/.test(content[i])) i++;
  if (content[i] === ';') i++;
  // Consume trailing newline.
  if (content[i] === '\r') i++;
  if (content[i] === '\n') i++;

  return i;
}

/**
 * Walks backward from `idx` over contiguous comment-only / blank lines and returns
 * the cut point. This lets us also remove the section header comments that often
 * sit just above an ad-hoc Hangfire block.
 */
function startOfBlock(content: string, idx: number): number {
  // Find the start of the line containing idx.
  let lineStart = content.lastIndexOf('\n', idx - 1) + 1;
  // Walk backward line by line over blank or comment lines.
  while (lineStart > 0) {
    const prevLineEnd = lineStart - 1;
    const prevLineStart = content.lastIndexOf('\n', prevLineEnd - 1) + 1;
    const prevLine = content.slice(prevLineStart, prevLineEnd).replace(/\r$/, '');
    const trimmed = prevLine.trim();
    if (trimmed === '' || trimmed.startsWith('//')) {
      lineStart = prevLineStart;
    } else {
      break;
    }
  }
  return lineStart;
}

function skipString(content: string, startIdx: number): number {
  // content[startIdx] === '"' — find the matching un-escaped '"'.
  let i = startIdx + 1;
  while (i < content.length) {
    if (content[i] === '\\') {
      i += 2;
      continue;
    }
    if (content[i] === '"') return i + 1;
    i++;
  }
  return content.length;
}

/**
 * Inserts the canonical `AddSmartStackHangfire` call before `AddSmartStack`
 * and the dashboard middleware after `UseSmartStack`. No-ops if already present.
 */
function wireSmartStackHangfire(content: string): string {
  let result = content;

  if (!/builder\.Services\.AddSmartStack\s*\(/.test(result)) {
    return result;
  }

  if (!result.includes('AddSmartStackHangfire')) {
    result = ensureUsingDirective(
      result,
      'SmartStack.Infrastructure.Persistence',
      'SmartStack.Api.Extensions',
    );

    result = result.replace(
      /^([ \t]*)(?:(?:\/\/\s*={3,}\s*\r?\n\s*\/\/[^\r\n]*\r?\n\s*\/\/\s*={3,}\s*\r?\n[ \t]*)?)builder\.Services\.AddSmartStack\s*\(/m,
      (_match, indent: string) => {
        const i = indent || '    ';
        const block =
          `${i}// R6 — Hangfire (durable workflow resume scheduling). Wired BEFORE AddSmartStack\n` +
          `${i}// because IBackgroundJobClient must be available when Infrastructure DI registers\n` +
          `${i}// IWorkflowResumeScheduler.\n` +
          `${i}builder.Services.AddSmartStackHangfire(builder.Configuration);\n\n` +
          `${i}builder.Services.AddSmartStack(`;
        return block;
      },
    );
  }

  if (
    !result.includes('UseSmartStackHangfireDashboard') &&
    /app\.UseSmartStack\s*\(\s*\)\s*;/.test(result)
  ) {
    result = result.replace(
      /^([ \t]*)app\.UseSmartStack\s*\(\s*\)\s*;\s*\r?\n/m,
      (match, indent: string) => {
        const i = indent || '    ';
        return (
          match +
          `\n${i}// R6 — Hangfire dashboard (admin-only). Mounted AFTER UseAuthentication (inside UseSmartStack)\n` +
          `${i}// so the dashboard filter sees the authenticated user principal.\n` +
          `${i}app.UseSmartStackHangfireDashboard(app.Environment, app.Configuration);\n`
        );
      },
    );
  }

  return result;
}

/**
 * Inserts `using <namespace>;` if absent. When `afterNamespace` is provided, the new
 * directive is placed immediately after that one to keep grouping consistent.
 */
function ensureUsingDirective(
  content: string,
  namespaceToAdd: string,
  afterNamespace?: string,
): string {
  const directive = `using ${namespaceToAdd};`;
  if (content.includes(directive)) return content;

  if (afterNamespace) {
    const anchor = `using ${afterNamespace};`;
    if (content.includes(anchor)) {
      return content.replace(anchor, `${anchor}\n${directive}`);
    }
  }

  const usingMatch = content.match(/^using [^;]+;/m);
  if (usingMatch) {
    return content.replace(usingMatch[0], `${directive}\n${usingMatch[0]}`);
  }

  return content;
}
